import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Backdrop,
  CircularProgress,
  DialogActions,
} from "@mui/material";
import styled from "styled-components";
import api from "../../api/config";
import { useNotification } from "../../context/NotificationContext";
import TableWithPagination from "./TableWithPagination";
import StyledTextField from "./StyledTextField";
import { CreateAccessLevel, UpdateUserAccessLevel } from "../../api/Access";

const Header = styled(DialogTitle)`
  background-color: #28929c;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
`;

const Content = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px !important;
  background-color: #2b3342;
  color: white;
`;

const Footer = styled(DialogActions)`
  background-color: #28929c;
  padding: 10px;
`;

export default function AddAccessLevelModal({
  open,
  onClose,
  onSaveSuccess = () => {},
  isEdit = false,
  selectedAccessLevel = {},
}) {
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();
  const [accessName, setAccessName] = useState("");
  const [accessDescription, setAccessDescription] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [accessUUID, setAccessUUID] = useState(null);

  const permissionsColumns = [{ id: "form", label: "Name" }];
  const permissionsTableData = [
    {
      id: 1,
      form: "Requests",
      name: "addRequest",
    },
    {
      id: 2,
      form: "Send Patient Chart",
      name: "sendPatientChart",
    },
    {
      id: 3,
      form: "View Files",
      name: "viewFiles",
    },
    {
      id: 4,
      form: "Upload Files",
      name: "uploadFiles",
    },
    {
      id: 5,
      form: "Digital Fax",
      name: "digitalFax",
    },
    {
      id: 6,
      form: "Share Patient Chart",
      name: "sharePatientChart",
    },
    {
      id: 7,
      form: "De_Identification Module",
      name: "deidentificationModule",
    },
    {
      id: 8,
      form: "Insurance Eligbility",
      name: "insuranceEligbility",
    },

    {
      id: 9,
      form: "Patient360",
      name: "patient360",
    },
    {
      id: 10,
      form: "Form Builder",
      name: "formBuilder",
    },
    {
      id: 11,
      form: "Delete Patient",
      name: "deletePatient",
    },
    {
      id: 12,
      form: "aibot",
      name: "aibot",
    },
    {
      id: 13,
      form: "Dashboard",
      name: "dashboard",
    },
    {
      id: 14,
      form: "Dashboard Extension",
      name: "dashboardExtension",
    },
    {
      id: 15,
      form: "Billing",
      name: "billing",
    },
    {
      id: 16,
      form: "Access Level Settings",
      name: "accessLevelSettings",
    },
    {
      id: 17,
      form: "My Images",
      name: "myimages",
    },
    {
      id: 18,
      form: "Add New Tenant",
      name: "addNewTenant",
    },
    {
      id: 19,
      form: "Update Tenant",
      name: "updateTenant",
    },
    {
      id: 20,
      form: "Access Other Tenants",
      name: "accessOtherTenants",
    },
    {
      id: 21,
      form: "AllAudit Logs",
      name: "allAuditLogs",
    },
  ];

  useEffect(() => {
    if (isEdit && selectedAccessLevel) {
      setAccessName(selectedAccessLevel.name || "");
      setAccessDescription(selectedAccessLevel.description || "");
      setAccessUUID(selectedAccessLevel.uuid || null);

      const selectedPermissions = permissionsTableData.filter(
        (item) => selectedAccessLevel.permissions[item.name] === true
      );
      setSelectedRows(selectedPermissions);
    } else {
      setAccessName("");
      setAccessDescription("");
      setSelectedRows([]);
      setAccessUUID(null);
    }
  }, [isEdit, selectedAccessLevel]);

  const handleSave = async () => {
    setLoading(true);

    try {
      const permissions = permissionsTableData.reduce((acc, item) => {
        acc[item.name] = selectedRows.some(
          (selected) => selected.id === item.id
        );
        return acc;
      }, {});

      const payload = {
        name: accessName,
        description: accessDescription,
        permissions,
      };

      const clientUuid = localStorage.getItem("clientuuid");

      if (isEdit && accessUUID) {
        const response = await api.post(
          `${UpdateUserAccessLevel}/${clientUuid}/${accessUUID}?accessLevelUID=${accessUUID}`,
          payload
        );
        showNotification("Access Level Updated", "success");
      } else {
        const response = await api.post(
          `${CreateAccessLevel}/${clientUuid}`,
          payload
        );
        showNotification("Access Level Created", "success");
      }

      onSaveSuccess();
      handleClose();
    } catch (error) {
      console.error("Error saving access level:", error);
      showNotification("Error saving access level", "error");
    }

    setLoading(false);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <Header>Add Access Level</Header>

      <Content>
        <div style={{ display: "flex", gap: 16 }}>
          <StyledTextField
            required
            autoFocus
            margin="dense"
            label="Access Name"
            fullWidth
            variant="outlined"
            value={accessName}
            onChange={(e) => setAccessName(e.target.value)}
          />
          <StyledTextField
            required
            autoFocus
            margin="dense"
            label="Access Description"
            fullWidth
            variant="outlined"
            value={accessDescription}
            onChange={(e) => setAccessDescription(e.target.value)}
          />
        </div>

        <TableWithPagination
          title="Access Level Permissions"
          columns={permissionsColumns}
          rows={permissionsTableData}
          noSearch={true}
          selectableRows
          preSelectedRows={selectedRows}
          onSelectedRowsChange={(selectedRows) => {
            setSelectedRows(selectedRows);
            console.log("Selected Rows:", selectedRows);
          }}
          height="450px"
        />
      </Content>

      <Footer>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave}>
          {isEdit ? "Update Access Level" : "Save Access Level"}
        </Button>
      </Footer>

      <Backdrop open={loading} style={{ zIndex: 1300, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}
